import { useCallback, useEffect, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 300;

export interface SearchOption {
  value: string;
  label: string;
}

/** `pinned` ahead of `found`, skipping any option already there. */
function withPinned<T extends SearchOption>(pinned: T | undefined, found: T[]): T[] {
  if (!pinned) return found;
  return [pinned, ...found.filter((option) => option.value !== pinned.value)];
}

/**
 * Options for a remote-search Select. It opens on the server's first page, before anything is typed,
 * so an empty picker still shows there are choices; typing searches the server, debounced, and closing
 * the picker brings back the first page. Only the newest request's answer is kept, so a slow search
 * can't overwrite a later one, and a failed one keeps the options already shown. `pin` keeps the
 * selected record at the top of the first page (a form reopened on a value the first page doesn't
 * hold), so its label shows rather than its id.
 *
 * `fetchOptions` must be stable (`useCallback`): a new one reloads the first page.
 */
export function useRemoteSearchOptions<T extends SearchOption>(fetchOptions: (q: string | undefined) => Promise<T[]>) {
  const [found, setFound] = useState<T[]>([]);
  /** The term `found` answers: options shown for a search, or the first page when empty. */
  const [foundFor, setFoundFor] = useState('');
  const [pinned, setPinned] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const latestRequest = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchLatest = useCallback(
    (term: string) => {
      const request = ++latestRequest.current;
      const isLatest = () => request === latestRequest.current;
      // Through a resolved promise, so a fetch that throws before returning one is caught like a rejected one.
      Promise.resolve()
        .then(() => fetchOptions(term.trim() || undefined))
        .then((options) => {
          if (!isLatest()) return;
          setFound(options);
          setFoundFor(term);
        })
        .catch(() => undefined)
        .finally(() => {
          if (isLatest()) setLoading(false);
        });
    },
    [fetchOptions],
  );

  useEffect(() => {
    const requests = latestRequest;
    const debounce = debounceRef;
    fetchLatest('');
    return () => {
      requests.current++;
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [fetchLatest]);

  function load(term: string) {
    setLoading(true);
    fetchLatest(term);
  }

  function onSearch(term: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(term), SEARCH_DEBOUNCE_MS);
  }

  function onOpenChange(open: boolean) {
    if (open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (foundFor) load('');
  }

  return {
    pin: setPinned,
    selectProps: {
      showSearch: true,
      filterOption: false,
      loading,
      onSearch,
      onOpenChange,
      options: foundFor ? found : withPinned(pinned, found),
    },
  };
}
